import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiFileText } from 'react-icons/fi';
import { getOffering } from '../../services/api';
import { PageHeader } from '../../components/common';
import { fmtCurrency } from '../../utils/formatNumber';
import { formatDate } from '../../utils/dateUtils';
import styles from '../Receipts/ReceiptDetailPage.module.css';

export default function OfferingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [offering, setOffering] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getOffering(id);
      setOffering(res.data);
    } catch {
      toast.error('Offering not found');
      navigate('/accounting/offerings');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!offering) return null;

  const memberName = offering.member?.name || offering.creator?.name || '—';
  const totalSettled = (offering.settlements || []).reduce((sum, settlement) => (
    sum + (parseFloat(settlement.settledAmount) || 0)
  ), 0);

  return (
    <div className={styles.page}>
      <PageHeader
        icon={<FiFileText />}
        title="Offering Detail"
        actions={
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Cancel
          </button>
        }
      />

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className={styles.headerBand}>
          <span className={styles.receiptNo}>{offering.offerNo || `OFR-${offering.id}`}</span>
          <div className={styles.badgeRow}>
            <span className={styles.typeBadge} style={{ textTransform: 'capitalize' }}>
              {offering.status || 'pending'}
            </span>
          </div>
        </div>

        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Offering Date</span>
            <span className={styles.infoValue}>{formatDate(offering.offeringDate)}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Member</span>
            <span className={styles.infoValue}>{memberName}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Category</span>
            <span className={styles.infoValue}>{offering.DonationCategory?.name || '—'}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Offered Amount</span>
            <span className={styles.amountValue}>{fmtCurrency(offering.offeredAmount)}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Paid Amount</span>
            <span className={styles.infoValue}>{fmtCurrency(offering.paidAmount)}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Pending Amount</span>
            <span className={styles.infoValue}>{fmtCurrency(offering.pendingAmount)}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Financial Year</span>
            <span className={styles.infoValue}>{offering.FinancialYear?.yearLabel || '—'}</span>
          </div>
          {offering.notes && (
            <div className={styles.infoItem} style={{ gridColumn: '1 / -1' }}>
              <span className={styles.infoLabel}>Notes</span>
              <span className={styles.infoValue}>{offering.notes}</span>
            </div>
          )}
          {offering.cancellationReason && (
            <div className={styles.infoItem} style={{ gridColumn: '1 / -1' }}>
              <span className={styles.infoLabel}>Cancellation Reason</span>
              <span className={styles.infoValue}>{offering.cancellationReason}</span>
            </div>
          )}
        </div>

        {offering.settlements?.length > 0 && (
          <>
            <div className={styles.divider} />
            <div className={styles.settlementsSection}>
              <div className={styles.sectionTitle}>Receipts Linked</div>
              <table className={styles.miniTable}>
                <thead>
                  <tr>
                    <th>Receipt No</th>
                    <th>Receipt Date</th>
                    <th>Settled (Rs)</th>
                  </tr>
                </thead>
                <tbody>
                  {offering.settlements.map((settlement) => (
                    <tr key={`${settlement.receiptId}-${settlement.offeringId}`}>
                      <td>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ padding: '2px 6px', fontFamily: 'monospace', fontWeight: 600 }}
                          onClick={() => navigate(`/accounting/receipts/${settlement.receiptId}`)}
                        >
                          {settlement.Receipt?.receiptNo || `#${settlement.receiptId}`}
                        </button>
                      </td>
                      <td>{settlement.Receipt?.receiptDate ? formatDate(settlement.Receipt.receiptDate) : '—'}</td>
                      <td>{fmtCurrency(settlement.settledAmount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>Total Settled</td>
                    <td>{fmtCurrency(totalSettled)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}

        <div className={styles.actionBar}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Back
          </button>
        </div>
      </div>
    </div>
  );
}
